'use client';

import { useCallback, useEffect, useState } from 'react';

import { useTranslations } from 'next-intl';

import WalletIcon from '@/assets/icons/wallet.svg';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useTokenImport } from '@/hooks/useTokenImport';
import { useWalletConnection } from '@/hooks/useWalletConnection';
import { useRouter } from '@/i18n/navigation';
import { useSignOut } from '@/lib/auth/useSignOut.client';
import { cn } from '@/lib/utils/cn';
import { useGlobalData } from '@/providers/GlobalDataProvider';
import { C_Routes } from '@/ts/constants/C_Routes';
import {
  C_TXONE_TOKEN_ADDRESS,
  C_TXONE_DECIMALS
} from '@/ts/constants/C_Staking';
import { E_Routes } from '@/ts/enums/E_Routes';

interface I_HeaderWalletButtonProps {
  onWalletClick: () => void;
  type?: 'default' | 'reward';
}

export function HeaderWalletButton({
  type,
  onWalletClick
}: I_HeaderWalletButtonProps) {
  const isReward = type === 'reward';
  const [mounted, setMounted] = useState(false);
  const { isFullyLoggedIn, isLoading } = useWalletConnection();
  const user = useGlobalData('user');
  const router = useRouter();
  const signOut = useSignOut();

  // Always call hooks at the top level
  const tWallet = useTranslations('wallet');
  const tPools = useTranslations('pools');
  const tAccessibility = useTranslations('accessibility');

  // Token import hook
  const { importTokenToWallet, isImporting } = useTokenImport({
    tokenAddress: C_TXONE_TOKEN_ADDRESS,
    tokenSymbol: 'TxONE',
    fallbackDecimals: C_TXONE_DECIMALS
  });

  const handleImportToken = useCallback(async () => {
    await importTokenToWallet();
  }, [importTokenToWallet]);

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      router.push(C_Routes[E_Routes.HOME].link);
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  }, [signOut, router]);

  // Prevent hydration mismatch by only rendering after mount
  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return (
      <Button
        variant="secondary"
        className="flex bg-transparent items-center justify-center w-9 h-9 rounded-full cursor-pointer transition-colors p-0"
        aria-hidden="true"
        tabIndex={-1}
        disabled
      />
    );
  }

  const walletIconAriaLabel = isFullyLoggedIn
    ? tWallet('connected')
    : tWallet('connect');

  // Show dropdown for reward type when user is logged in
  if (isReward && isFullyLoggedIn && !isLoading) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="secondary"
            className={cn(
              'flex bg-transparent items-center justify-center w-9 h-9 rounded-full cursor-pointer transition-colors p-0'
            )}
            aria-label={walletIconAriaLabel}
          >
            <UserAvatar
              src={user?.avatar?.url}
              alt={tAccessibility('avatar')}
              width={36}
              height={36}
              className="h-9 w-9 rounded-full"
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-45 bg-background">
          <DropdownMenuItem
            onClick={handleImportToken}
            disabled={isImporting}
            className="cursor-pointer"
          >
            {isImporting ? 'Importing...' : tPools('importTokenInWallet')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={handleSignOut}
            className="cursor-pointer text-destructive focus:text-destructive"
          >
            {tWallet('signOut')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <Button
      variant="secondary"
      onClick={onWalletClick}
      className={cn(
        'flex bg-transparent items-center justify-center w-9 h-9 rounded-full cursor-pointer transition-colors p-0',
        !isFullyLoggedIn && 'md:hidden',
        isReward && !isFullyLoggedIn ? 'hidden' : ''
      )}
      aria-label={walletIconAriaLabel}
      aria-busy={isLoading}
      disabled={isLoading}
    >
      <>
        {isLoading ? (
          <div
            className="w-6 h-6 rounded-full border-2 border-gray-300 border-t-primary-border animate-spin"
            role="status"
            aria-label={tAccessibility('loadingWallet')}
          />
        ) : isFullyLoggedIn ? (
          <UserAvatar
            src={user?.avatar?.url}
            alt={tAccessibility('avatar')}
            width={36}
            height={36}
            className="h-9 w-9 rounded-full"
          />
        ) : (
          <WalletIcon className="h-6 w-6 md:hidden text-foreground" />
        )}
      </>
    </Button>
  );
}
